import { computeLineDiff, tokenizeWords, type DiffLine } from '../utils/lineDiff'

/**
 * Adapts manuscript HTML to the line diff in utils/lineDiff.
 *
 * Regex rather than DOMParser on purpose: this keeps the module pure so it can be unit tested
 * in node without pulling jsdom into the project.
 */

/** Which block element a line came from, so the diff can render it as the writer wrote it. */
export type ManuscriptBlockKind = 'paragraph' | 'bullet' | 'number' | 'quote' | 'heading'

export interface ManuscriptLine {
  kind: ManuscriptBlockKind
  text: string
  /** 1-based position within its own ordered list; only set when kind is 'number'. */
  ordinal?: number
}

export interface ManuscriptDiffRow extends ManuscriptLine {
  change: 'same' | 'added' | 'removed'
  /**
   * Word-level diff of this row against its counterpart, present only when a single removed
   * line sits directly opposite a single added one — i.e. a rewrite rather than a wholesale
   * insertion or deletion.
   */
  words?: DiffLine[]
}

// Matches one block element and its inner HTML. The backreference keeps nesting sane:
// <blockquote><p>x</p></blockquote> matches the blockquote and yields one line, because the
// scan resumes past the whole match rather than descending into the inner <p>.
const BLOCK_ELEMENT = /<(p|div|blockquote|li|h[1-6])\b[^>]*>([\s\S]*?)<\/\1\s*>/gi
const ORDERED_LIST = /<ol\b[^>]*>[\s\S]*?<\/ol\s*>/gi

const decodeEntities = (value: string): string =>
  value
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    // &amp; is decoded last so "&amp;lt;" survives as the literal text "&lt;".
    .replace(/&amp;/gi, '&')

const toText = (html: string): string =>
  decodeEntities(html.replace(/<[^>]*>/g, ''))
    .replace(/[ \t]+/g, ' ')
    .trim()

const blockKindFor = (tag: string, isInsideOrderedList: boolean): ManuscriptBlockKind => {
  const name = tag.toLowerCase()
  if (name === 'blockquote') return 'quote'
  if (name === 'li') return isInsideOrderedList ? 'number' : 'bullet'
  if (/^h[1-6]$/.test(name)) return 'heading'
  return 'paragraph'
}

/**
 * Split manuscript HTML into one entry per rendered line, each tagged with the block element
 * it came from. The diff needs the tag as well as the text: a bullet, a quotation and an
 * ordinary paragraph all flatten to the same bare string otherwise, and the Changes view then
 * shows a list as though the writer had typed loose sentences.
 */
export function htmlToManuscriptLines(html: string): ManuscriptLine[] {
  const orderedRanges: Array<[number, number]> = []
  for (const match of html.matchAll(ORDERED_LIST)) {
    orderedRanges.push([match.index, match.index + match[0].length])
  }
  const isOrdered = (index: number): boolean =>
    orderedRanges.some(([start, end]) => index >= start && index < end)

  const lines: ManuscriptLine[] = []
  const ordinals = new Map<number, number>()
  let lastEnd = 0

  const pushBlock = (kind: ManuscriptBlockKind, innerHtml: string, ordinal?: number): void => {
    // A <br> is a line break inside one block, so it yields several lines of the same kind.
    for (const piece of innerHtml.split(/<br\s*\/?>/i)) {
      const text = toText(piece)
      // Blank paragraphs are formatting, not content — diffing them produces noise the writer
      // cannot act on.
      if (text) lines.push(ordinal === undefined ? { kind, text } : { kind, text, ordinal })
    }
  }

  for (const match of html.matchAll(BLOCK_ELEMENT)) {
    // Bare text sitting between blocks still reads as a paragraph to the writer.
    pushBlock('paragraph', html.slice(lastEnd, match.index))

    const ordered = match[1].toLowerCase() === 'li' && isOrdered(match.index)
    let ordinal: number | undefined
    if (ordered) {
      const listStart = orderedRanges.find(
        ([start, end]) => match.index >= start && match.index < end
      )![0]
      const next = (ordinals.get(listStart) ?? 0) + 1
      ordinals.set(listStart, next)
      ordinal = next
    }
    pushBlock(blockKindFor(match[1], ordered), match[2], ordinal)
    lastEnd = match.index + match[0].length
  }
  pushBlock('paragraph', html.slice(lastEnd))

  return lines
}

/** Just the text of each line, for word counts and anywhere the block kind is irrelevant. */
export function htmlToDiffLines(html: string): string[] {
  return htmlToManuscriptLines(html).map((line) => line.text)
}

/**
 * Visible text of a manuscript, with block boundaries preserved as single spaces.
 *
 * The editor's word and character counts use this rather than DOMParser's textContent, which
 * concatenates straight across block boundaries: "<div>The lamp</div><div>guttered</div>"
 * becomes "The lampguttered" and counts as one word. The history dialog's counts come from the
 * server's htmlToPlainText, which does insert a separator, so reading textContent here made
 * the two numbers disagree by roughly one per paragraph on the very same chapter.
 */
export function htmlToPlainText(html: string): string {
  return htmlToDiffLines(html).join(' ')
}

/**
 * Diff two manuscripts into rows that keep their block kind.
 *
 * The LCS itself still runs on plain text via computeLineDiff, so a line that only changed
 * its formatting is not reported as an edit. The block kind is recovered afterwards by
 * walking the original line arrays in step with the diff result — each entry consumes one
 * old line, one new line, or both — which needs no encoding of the kind into the compared
 * strings and so cannot perturb the comparison.
 */
export function buildManuscriptDiffRows(oldHtml: string, newHtml: string): ManuscriptDiffRow[] {
  const oldLines = htmlToManuscriptLines(oldHtml)
  const newLines = htmlToManuscriptLines(newHtml)
  const diff = computeLineDiff(
    oldLines.map((line) => line.text),
    newLines.map((line) => line.text)
  )

  const rows: ManuscriptDiffRow[] = []
  let oldIndex = 0
  let newIndex = 0
  for (const entry of diff) {
    if (entry.type === 'removed') {
      rows.push({ ...oldLines[oldIndex], change: 'removed' })
      oldIndex += 1
    } else if (entry.type === 'added') {
      rows.push({ ...newLines[newIndex], change: 'added' })
      newIndex += 1
    } else {
      rows.push({ ...newLines[newIndex], change: 'same' })
      oldIndex += 1
      newIndex += 1
    }
  }

  return attachWordDiffs(rows)
}

/**
 * Give a rewritten line word-level highlights. A lone removed row directly followed by a lone
 * added row is a rewrite, so only the words that actually changed need marking; runs of
 * several removals or additions are left plain, because pairing those would be guessing which
 * line replaced which.
 */
function attachWordDiffs(rows: ManuscriptDiffRow[]): ManuscriptDiffRow[] {
  const result = [...rows]
  for (let index = 0; index < result.length - 1; index++) {
    const current = result[index]
    const next = result[index + 1]
    const isLonePair =
      current.change === 'removed' &&
      next.change === 'added' &&
      result[index - 1]?.change !== 'removed' &&
      result[index + 2]?.change !== 'added'
    if (!isLonePair) continue

    const words = computeLineDiff(tokenizeWords(current.text), tokenizeWords(next.text))
    result[index] = { ...current, words: words.filter((word) => word.type !== 'added') }
    result[index + 1] = { ...next, words: words.filter((word) => word.type !== 'removed') }
    index += 1
  }
  return result
}
